import React, { useEffect, useState } from 'react';
import CommonModal from './CommonModal';
import { callAPI } from '../../utils/apiUtils';
import { apiUrls } from '../../utils/apiUrls';
import { toast } from 'react-toastify';

const WEIGHT_UNITS = ['g', 'kg', 'oz', 'lb'];
const PRICING_UNIT_DENOMINATORS = [1, 10, 100, 1000];

const ToggleSwitch = ({ checked, onChange }) => (
  <label className="toggleSwitch">
    <input type="checkbox" checked={checked} onChange={onChange} />
    <span className="toggleSlider" />
  </label>
);

/**
 * Create/edit a weighted product - name, weight unit, pricing unit denominator, price per unit,
 * enabled toggle. Doubles as both "Add" and "Edit" - `product` is null in create mode, matching
 * ProductEditModal.js's convention.
 */
const WeightedProductEditModal = ({ isOpen, product, onClose, onSaved }) => {
  const isCreateMode = !product;

  const [name, setName] = useState('');
  const [weightUnit, setWeightUnit] = useState('g');
  const [pricingUnitDenominator, setPricingUnitDenominator] = useState(100);
  const [pricePerUnit, setPricePerUnit] = useState('');
  const [status, setStatus] = useState(1);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (product) {
      setName(product.name || '');
      setWeightUnit(product.weight_unit || 'g');
      setPricingUnitDenominator(product.pricing_unit_denominator || 100);
      setPricePerUnit(product.price_per_unit != null ? String(product.price_per_unit) : '');
      setStatus(product.status);
    } else {
      setName('');
      setWeightUnit('g');
      setPricingUnitDenominator(100);
      setPricePerUnit('');
      setStatus(1);
    }
  }, [product, isOpen]);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!name.trim()) {
      toast.warning('Product name cannot be empty');
      return;
    }
    if (!pricePerUnit || isNaN(pricePerUnit) || Number(pricePerUnit) < 0) {
      toast.warning('Enter a valid price per unit');
      return;
    }

    setSaving(true);
    try {
      if (isCreateMode) {
        const res = await callAPI(apiUrls.WEIGHTED_PRODUCTS_CREATE, {}, 'POST', {
          name: name.trim(),
          weight_unit: weightUnit,
          pricing_unit_denominator: pricingUnitDenominator,
          price_per_unit: parseFloat(pricePerUnit),
        }, {});

        toast.success('Weighted product created');
        onSaved(res.data?.data, true);
      } else {
        const res = await callAPI(apiUrls.WEIGHTED_PRODUCTS_UPDATE, {}, 'POST', {
          id: product.id,
          name: name.trim(),
          weight_unit: weightUnit,
          pricing_unit_denominator: pricingUnitDenominator,
          price_per_unit: parseFloat(pricePerUnit),
          status,
        }, {});

        toast.success('Weighted product updated');
        onSaved(res.data?.data, false);
      }
    } catch (err) {
      console.error('Failed to save weighted product', err);
      toast.error('Failed to save weighted product');
    } finally {
      setSaving(false);
    }
  };

  return (
    <CommonModal isOpen={isOpen} onClose={onClose}>
      <div className="checkinFormCard">
        <h4 className="lgTitle pb-3 text-center">{isCreateMode ? 'Add Weighted Product' : 'Edit Weighted Product'}</h4>

        <div className="formStyle">
          <div className="row gx-2 gy-3">
            <div className="col-12">
              <label className="form-label">Product Name</label>
              <input
                type="text"
                className="form-control"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div className="col-6">
              <label className="form-label">Weight Unit</label>
              <select
                className="form-control"
                value={weightUnit}
                onChange={(e) => setWeightUnit(e.target.value)}
              >
                {WEIGHT_UNITS.map((unit) => (
                  <option key={unit} value={unit}>{unit}</option>
                ))}
              </select>
            </div>

            <div className="col-6">
              <label className="form-label">Pricing Unit</label>
              <select
                className="form-control"
                value={pricingUnitDenominator}
                onChange={(e) => setPricingUnitDenominator(Number(e.target.value))}
              >
                {PRICING_UNIT_DENOMINATORS.map((denom) => (
                  <option key={denom} value={denom}>{denom}</option>
                ))}
              </select>
            </div>

            <div className="col-12">
              <label className="form-label">Price per Unit</label>
              <input
                type="number"
                step="0.01"
                min="0"
                className="form-control"
                placeholder={`Price per ${pricingUnitDenominator}${weightUnit}`}
                value={pricePerUnit}
                onChange={(e) => setPricePerUnit(e.target.value)}
              />
            </div>

            {!isCreateMode && (
              <div className="col-12 d-flex align-items-center gap-2">
                <ToggleSwitch checked={status === 1} onChange={(e) => setStatus(e.target.checked ? 1 : 0)} />
                <span>{status === 1 ? 'Enabled' : 'Disabled'}</span>
              </div>
            )}

            <div className="col-12 text-center pt-2">
              <button className="btn btn-secondary me-2" onClick={onClose}>
                Cancel
              </button>
              <button
                className="btn iconBtn btnPrimary"
                onClick={handleSave}
                disabled={saving}
              >
                <span>{saving ? 'Saving...' : 'Save'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </CommonModal>
  );
};

export default WeightedProductEditModal;
